import { StockData } from '../types/stock';
import { getConfig } from '../utils/config';
import { logger } from '../utils/logger';

export interface YahooFinanceMessage {
  type: string;
  symbol?: string;
  data?: any;
  timestamp?: number;
  source?: string;
  service?: string;
  connected?: boolean;
  error?: string;
}

// Singleton instance to prevent multiple connections
let globalServiceInstance: YahooFinanceWebSocketService | null = null;

export class YahooFinanceWebSocketService {
  private ws: WebSocket | null = null;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  private pendingHistoricalData: { symbol: string; data: StockData[] } | null = null;

  constructor(
    private onStockUpdate: (stock: StockData) => void,
    private onConnectionChange: (connected: boolean) => void,
    private onError: (error: string) => void,
    private onHistoricalData?: (symbol: string, data: StockData[]) => void
  ) {}

  static getInstance(
    onStockUpdate: (stock: StockData) => void,
    onConnectionChange: (connected: boolean) => void,
    onError: (error: string) => void,
    onHistoricalData?: (symbol: string, data: StockData[]) => void
  ): YahooFinanceWebSocketService {
    if (!globalServiceInstance) {
      globalServiceInstance = new YahooFinanceWebSocketService(onStockUpdate, onConnectionChange, onError, onHistoricalData);
    } else {
      // Update callbacks for existing instance
      globalServiceInstance.onStockUpdate = onStockUpdate;
      globalServiceInstance.onConnectionChange = onConnectionChange;
      globalServiceInstance.onError = onError;
      globalServiceInstance.onHistoricalData = onHistoricalData;

      logger.log(`🔧 Updated callbacks - onHistoricalData:`, !!onHistoricalData);

      // Process any pending historical data
      if (globalServiceInstance.pendingHistoricalData && onHistoricalData) {
        logger.log(`📊 Processing pending historical data for ${globalServiceInstance.pendingHistoricalData.symbol}`);
        onHistoricalData(globalServiceInstance.pendingHistoricalData.symbol, globalServiceInstance.pendingHistoricalData.data);
        globalServiceInstance.pendingHistoricalData = null;
      }
    }
    return globalServiceInstance;
  }

  connect() {
    // Prevent multiple connections
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    // Clean up any existing connection first
    if (this.ws) {
      this.disconnect();
    }

    this.isConnecting = true;

    // 从配置获取 Stock WebSocket URL
    const config = getConfig();
    const wsUrl = process.env.REACT_APP_YAHOO_WS_URL || config.STOCK_WS_URL || 'wss://getaws.alpha-pilot.net/stock_ws/';
    logger.log('🔌 Attempting to connect to:', wsUrl);
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      logger.log('✅ Yahoo Finance WebSocket connected successfully');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.onConnectionChange(true);

      // Send a status request to get current data
      this.sendMessage({
        type: 'get_status'
      });

      // Historical data will be requested by the hook when needed
    };

    this.ws.onmessage = (event) => {
      try {
        const data: YahooFinanceMessage = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        logger.error('Error parsing Yahoo Finance WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      logger.log(`🔌 Yahoo Finance WebSocket closed: code=${event.code}, reason=${event.reason}`);

      // Only notify disconnection for abnormal cases
      if (event.code > 1006) {
        this.onConnectionChange(false);
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      logger.error('❌ Yahoo Finance WebSocket error:', error);
      this.isConnecting = false;
      this.onError('WebSocket connection failed');
    };
  }

  private handleMessage(data: YahooFinanceMessage) {
    const messageType = data.type;
    // Only log important messages to reduce spam
    if (messageType === 'historical_data' || messageType === 'historical_data_error') {
      logger.log(`📨 Yahoo Finance message: ${messageType}`, data);
    }

    switch (messageType) {
      case 'welcome':
        logger.log('📨 Yahoo Finance welcome message:', data);
        break;

      case 'stock_update':
        if ((data.source === 'yahoo_finance' || data.source === 'yahoo_finance_aggregated') && data.data) {
          const stockData = this.formatStockData(data.symbol || '', data.data);
          this.onStockUpdate(stockData);
        }
        break;

      case 'status_response':
        logger.log('📊 Yahoo Finance service status:', data);
        break;

      case 'historical_data':
        if (data.symbol && Array.isArray(data.data)) {
          logger.log(`📊 Processing historical data for ${data.symbol}: ${data.data.length} items`);
          logger.log(`📊 Sample data item:`, data.data[0]);

          const historicalData = data.data.map((item: any) => this.formatStockData(data.symbol!, item));
          logger.log(`📊 Formatted ${historicalData.length} historical data points for ${data.symbol}`);
          logger.log(`📊 Sample formatted item:`, historicalData[0]);

          if (this.onHistoricalData) {
            logger.log(`📊 Calling onHistoricalData callback for ${data.symbol}`);
            this.onHistoricalData(data.symbol, historicalData);
          } else {
            logger.warn('⚠️ onHistoricalData callback not set, queuing data for later');
            // Store the data for when callback is available
            this.pendingHistoricalData = { symbol: data.symbol, data: historicalData };
          }
        } else {
          logger.warn('⚠️ Invalid historical data format:', data);
        }
        break;

      case 'historical_data_error':
        logger.error(`❌ Historical data error for ${data.symbol}:`, data.error);
        if (this.onError) {
          this.onError(`Failed to load historical data for ${data.symbol}: ${data.error}`);
        }
        break;

      case 'service_status':
        const service = data.service || 'unknown';
        const connected = data.connected || false;
        logger.log(`🔗 ${service} service: ${connected ? 'Connected' : 'Disconnected'}`);
        break;

      case 'service_error':
        const errorService = data.service || 'unknown';
        const error = data.error || 'Unknown error';
        logger.error(`❌ ${errorService} error:`, error);
        this.onError(`${errorService}: ${error}`);
        break;

      case 'subscription_response':
        logger.log('📊 Subscription response:', data);
        break;

      default:
        logger.log('📨 Unknown Yahoo Finance message type:', messageType, data);
        break;
    }
  }

  private formatStockData(symbol: string, data: any): StockData {
    // Convert Yahoo Finance data format to StockData format
    // lightweight-charts expects time as Unix timestamp in seconds
    let timeValue = data.time || Math.floor(Date.now() / 1000);

    // If time is a very large number (milliseconds), convert to seconds
    if (timeValue > 1e10) {
      timeValue = Math.floor(timeValue / 1000);
    }

    const formattedData = {
      time: timeValue as any, // lightweight-charts expects this format
      open: Number(data.open) || Number(data.close) || 0,
      high: Number(data.high) || Number(data.close) || 0,
      low: Number(data.low) || Number(data.close) || 0,
      close: Number(data.close) || 0,
      volume: Number(data.volume) || 0,
      symbol: symbol,
      // Yahoo Finance specific fields
      change: data.change || 0,
      change_percent: data.change_percent || 0,
      exchange: data.exchange || '',
      market_hours: data.market_hours || 0
    };

    // Validate OHLC data
    if (formattedData.open <= 0 || formattedData.high <= 0 || formattedData.low <= 0 || formattedData.close <= 0) {
      logger.warn(`⚠️ Invalid OHLC data for ${symbol}:`, formattedData);
    }

    // Validate OHLC relationships
    if (formattedData.high < formattedData.low ||
        formattedData.high < formattedData.open ||
        formattedData.high < formattedData.close ||
        formattedData.low > formattedData.open ||
        formattedData.low > formattedData.close) {
      logger.warn(`⚠️ Invalid OHLC relationships for ${symbol}:`, formattedData);
    }

    return formattedData;
  }

  private sendMessage(message: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Only log when we've truly failed after many attempts
      logger.error('Yahoo Finance service unavailable after multiple attempts');
      this.onError('Service unavailable');
      return;
    }

    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000); // Max 30 seconds, start with 2s
    this.reconnectAttempts++;

    // Silent reconnection - no console logs
    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      // Remove event listeners to prevent memory leaks
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;

      // Only close if not already closed
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.onConnectionChange(false);
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN || false;
  }

  subscribeToSymbols(symbols: string[]) {
    this.sendMessage({
      type: 'subscribe',
      symbols: symbols
    });
  }

  unsubscribeFromSymbols(symbols: string[]) {
    this.sendMessage({
      type: 'unsubscribe',
      symbols: symbols
    });
  }

  requestStatus() {
    this.sendMessage({
      type: 'get_status'
    });
  }

  requestHistoricalData(symbol: string, period: string = '3mo', interval: string = '1m') {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = {
        type: 'get_historical',
        symbol: symbol,
        period: period,
        interval: interval
      };
      logger.log('📤 Sending historical data request:', message);
      this.ws.send(JSON.stringify(message));
    } else {
      logger.warn('⚠️ WebSocket not connected, cannot request historical data. State:', this.ws?.readyState);
      // If not connected, try to connect first
      if (!this.isConnecting) {
        logger.log('🔄 Attempting to reconnect...');
        this.connect();
      }
    }
  }

  processPendingHistoricalData() {
    if (this.pendingHistoricalData && this.onHistoricalData) {
      logger.log(`📊 Processing pending historical data for ${this.pendingHistoricalData.symbol}`);
      this.onHistoricalData(this.pendingHistoricalData.symbol, this.pendingHistoricalData.data);
      this.pendingHistoricalData = null;
    }
  }
}
